import { format, parseISO, isToday, isYesterday } from 'date-fns';
import { Link } from 'react-router-dom';

import { mostRecentThread } from '@/features/clinic/ClinicScreen';
import { track } from '@/lib/analytics';
import { useSupportThread, useThreads } from '@/lib/api/hooks';
import { ArrowInline } from '@/lib/Icon';
import { nativeSupportEnabled } from '@/features/native-support/types';

import { messagingHub, type Team } from '../messagingHub';
import { useMessagingUnread } from '../useMessagingUnread';
import { SupportInfo } from './SupportPane';
import { ProviderAvatars, SupportAvatar, providerNames, providerRoles, useProviders } from './TeamAvatars';

function stamp(iso: string): string {
  const d = parseISO(iso);
  if (isToday(d)) return format(d, 'h:mma');
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'M/d/yy');
}

/**
 * Clinic › Messages under the care-team messaging experiment: one card per
 * team instead of the single "Send a message" button. Each opens the same
 * Messages drawer at that team.
 */
export function ClinicTeamCards() {
  const unread = useMessagingUnread();
  const { data: threads } = useThreads();
  const { data: support } = useSupportThread();
  const thread = mostRecentThread(threads ?? []);
  const providers = useProviders();

  // The thread list may omit message bodies; fall back to its preview line
  // the way the classic ThreadPreview row does.
  const lastProvider = thread?.messages[thread.messages.length - 1];
  const providerPreview = lastProvider
    ? {
        meta: `${lastProvider.sender_role === 'patient' ? 'You' : lastProvider.sender_name} · ${stamp(lastProvider.sent_at)}`,
        body: lastProvider.body,
      }
    : thread?.preview
      ? { meta: stamp(thread.last_message_at), body: thread.preview }
      : null;
  const lastSupport = [...(support?.messages ?? [])]
    .reverse()
    .find((m) => m.sender_role !== 'system');

  return (
    <div className="flex flex-col gap-15" data-tour="clinic-composer">
      <div className="type-label-sm text-[#7C7C7C]">Who would you like to message?</div>
      <TeamCard
        team="providers"
        label="Providers"
        unread={unread.providers > 0}
        avatar={<ProviderAvatars providers={providers} />}
        name={providerNames(providers)}
        role={providerRoles(providers)}
        description={
          <>
            For questions about symptoms, side effects,{' '}
            <CardLink team="providers" to="/app/protocols">dosing</CardLink> and{' '}
            <CardLink team="providers" to="/app/protocols">your prescription</CardLink>.
          </>
        }
        preview={providerPreview}
        cta="Message your providers"
      />
      <TeamCard
        team="support"
        label="System Support"
        info={nativeSupportEnabled() ? <SupportInfo /> : undefined}
        unread={unread.support > 0}
        avatar={<SupportAvatar />}
        name="System Support"
        role="Instant answers. A teammate joins when needed."
        description={
          <>
            For questions about{' '}
            <CardLink team="support" to="/app/orders">orders</CardLink>,{' '}
            <CardLink team="support" to="/app/orders">shipping</CardLink>,{' '}
            <CardLink team="support" to="/app/account?focus=payment">billing</CardLink> and{' '}
            <CardLink team="support" to="/app/account">your account</CardLink>.
          </>
        }
        preview={
          lastSupport
            ? {
                meta: `${lastSupport.sender_role === 'patient' ? 'You' : lastSupport.sender_name || 'System Labs'} · ${stamp(lastSupport.created_at)}`,
                body: lastSupport.body,
              }
            : null
        }
        cta="Message System Support"
      />
      <p className="text-12 text-[#7C7C7C] text-center leading-140">
        Not sure which? Pick either. We’ll point you to the right team.
      </p>
    </div>
  );
}

/**
 * Inline shortcut in a card's description: patients who came to change a
 * card or check an order can go straight there instead of opening a chat.
 * `?focus=payment` scrolls Account to the Payment methods card.
 */
function CardLink({ team, to, children }: { team: Team; to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      onClick={() => track('clinic_card_link_clicked', { team, to })}
      className="underline underline-offset-2 text-ink hover:text-accent"
    >
      {children}
    </Link>
  );
}

function TeamCard({
  team,
  label,
  info,
  unread,
  avatar,
  name,
  role,
  description,
  preview,
  cta,
}: {
  team: Team;
  label: string;
  info?: React.ReactNode;
  unread: boolean;
  avatar: React.ReactNode;
  name: string;
  role: string;
  description: React.ReactNode;
  preview: { meta: string; body: string } | null;
  cta: string;
}) {
  return (
    <div className="border border-line-subtle bg-[var(--surface-raised)] flex flex-col">
      <div className="flex justify-between border-b border-line-subtle bg-[var(--surface-sunken)]">
        {info ? (
          <span className="flex items-center pl-15 py-10">
            <span className="type-label-sm text-[#7C7C7C]">{label}</span>
            {info}
          </span>
        ) : (
          <span className="type-label-sm text-[#7C7C7C] px-15 py-10">{label}</span>
        )}
        <span
          className={
            'type-label-sm px-10 py-3 min-w-[12rem] flex items-center justify-center ' +
            (unread
              ? 'bg-[var(--accent-hover)] text-white'
              : 'bg-[var(--surface-smoke)] text-ink-secondary')
          }
        >
          {unread ? 'NEW MESSAGE' : '✓ READ'}
        </span>
      </div>
      <div className="flex flex-col gap-12 p-15">
        <div className="flex items-center gap-10">
          {avatar}
          <div className="min-w-0">
            <div className="type-body-md text-ink truncate">{name}</div>
            <div className="text-12 text-[#7C7C7C] mt-3">{role}</div>
          </div>
        </div>
        <p className="type-body-sm text-ink-secondary leading-snug">{description}</p>
        {preview && (
          <div className="flex flex-col gap-4 pt-12 border-t border-line-subtle">
            <span className="font-mono uppercase text-10 text-ink-muted">{preview.meta}</span>
            <p className="type-body-sm text-ink-secondary leading-snug line-clamp-2">{preview.body}</p>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => messagingHub.open({ team, source: 'clinic_card' })}
        className="w-full flex items-center justify-between h-50 px-15 bg-black text-white font-body text-15 hover:bg-[#252525]"
      >
        <span>{cta}</span>
        <ArrowInline />
      </button>
    </div>
  );
}
